/**
 * Modal — the vendored centered-dialog composition (WEB TIER A · WT2).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * VENDORED, NOT INSTALLED. This is CRF-owned source in the shape the shadcn
 * sandbox measured as worth taking (memory `reference_shadcn_crf`, verdict
 * 2026-08-12): the COMPOSITION — Radix's focus trap, Escape handler,
 * `aria-hidden` on the rest of the page, scroll lock — with none of shadcn's
 * measured costs. There is no shadcn CLI in this repo and no `shadcn` runtime
 * import anywhere; `@radix-ui/react-dialog` is an individual package the app
 * already shipped. Because this file is owned source, `shadcn add` can never
 * revert the fixes folded in below (the sandbox measured exactly that revert).
 *
 * THE SHAPE IS THE ONE THE APP ALREADY HAND-WIRES TWICE: `admin/ConfirmDialog`
 * and `QuoteRecordPage`'s `DialogShell` both draw the same `bg-ink/40` scrim,
 * the same `radius/md` card on `bg/surface`, the same `--crf-shadow-overlay`,
 * the same `onOpenChange` busy-guard. This component IS that shape, made one
 * source of truth. Existing wirings migrate only when their file is otherwise
 * touched by a Tier A cluster (docs/WEB-TIER-A-LEDGER.md item 1) — WT2 itself
 * migrates no consumer.
 *
 * SANDBOX-MEASURED FIXES FOLDED AT VENDOR TIME (pinned by
 * `vendored-source.test.ts`):
 *  · dark styling arrives through the app's `:root[data-theme='dark']`
 *    `:where()` custom-variant (app.css:23) — tokens re-map themselves, so this
 *    file needs no `.dark` selector and must never grow one;
 *  · no `disabled:opacity-*` anywhere — disabled is bordered-inert;
 *  · hover/active states are AUTHORED tokens, never `/NN` alpha fades of a
 *    fill (the sandbox measured `hover:bg-primary/80` at 3.65:1);
 *  · radii are the four authored tokens only (8/12/16/999 — `rounded-md` here);
 *  · quiet text is `text-fg-secondary` (4.64/5.85), never `text-fg-muted`.
 *
 * BUSY REFUSES DISMISSAL, for `ConfirmDialog`'s recorded reason: cancelling a
 * mutation you have not started must be the easy path; closing a dialog whose
 * request is in flight hides the outcome of something already happening.
 */

import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Button } from './Button';

export interface ModalProps {
  open: boolean;
  /** The question or task, naming its target — "Revoke 'Warehouse PC'?" */
  title: string;
  /** Consequences, not ceremony. Optional: a form modal may not need one. */
  description?: ReactNode;
  /** Refuses Escape/overlay dismissal while a request is in flight. */
  busy?: boolean;
  onClose: () => void;
  /** Action row, right-aligned under the body. */
  footer?: ReactNode;
  children?: ReactNode;
  /** Widens the card; the default is the hand-wired dialogs' 440px. */
  className?: string;
}

export function Modal({
  open,
  title,
  description,
  busy = false,
  onClose,
  footer,
  children,
  className,
}: ModalProps): React.JSX.Element {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // Escape and outside pointer-downs both arrive here. `busy` refuses
        // both — see the header.
        if (!next && !busy) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40" />
        <Dialog.Content
          // Radix wires `aria-describedby` to its Description automatically;
          // when no description exists it must be cleared explicitly — that is
          // the documented way to say "the title is the whole accessible name"
          // (leaving it would point at a missing id and log a Radix warning).
          {...(description === undefined ? { 'aria-describedby': undefined } : {})}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[min(440px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2',
            'rounded-md border border-line bg-surface p-6 shadow-[var(--crf-shadow-overlay)]',
            className,
          )}
        >
          <Dialog.Title className="text-h3 text-fg">{title}</Dialog.Title>
          {description === undefined ? null : (
            <Dialog.Description className="mt-1 text-label text-fg-secondary">
              {description}
            </Dialog.Description>
          )}
          {children === undefined ? null : <div className="mt-4 flex flex-col gap-3">{children}</div>}
          {footer === undefined ? null : (
            <div className="mt-5 flex items-center justify-end gap-2">{footer}</div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface ConfirmModalProps {
  open: boolean;
  /** The question, naming the target: "Revoke 'Warehouse PC — Kinshasa'?" */
  title: string;
  /** What happens if they proceed — `ReactNode` for list-shaped consequences. */
  body: ReactNode;
  /** The verb on the button, saying what it does: "Revoke key". */
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * ConfirmModal — the destructive-action convenience, matching
 * `admin/ConfirmDialog`'s API so its call sites can migrate mechanically when
 * their files are otherwise touched.
 *
 * THE CONFIRM IS RED, NOT INDIGO — `ConfirmDialog`'s recorded reasoning: a
 * destructive confirm painted the action colour invites the reflex click.
 * `bg-danger-fg` is the AA-nudged `status/danger/text` #ca2525 used AS A FILL:
 * white on it measures 5.52:1, mode-invariant.
 *
 * THE HOVER IS AN AUTHORED TOKEN, NOT THE ALPHA FADE `ConfirmDialog` wears.
 * `ConfirmDialog` hovers `bg-danger-fg/90` and presses `/80` — the same
 * theme-dependent alpha-fade defect D1a retired from the primary Button.
 * Vendoring is the moment that fix is folded (rule 3 of the sandbox verdict):
 * hover AND active take `bg-brand-hover` — the Figma-authored darker step of
 * this same red family (`brand/primary-hover` #b91f1f · VariableID:1:13;
 * `brand/primary` #da2828 is the un-nudged Figma value of `status/danger/text`
 * itself, so the family is the design's own). White on #b91f1f = 6.41:1 —
 * contrast RISES under the pointer, both modes, no opacity modifier. No third
 * authored red step exists, so active deliberately holds the hover value
 * rather than inventing one.
 */
export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: ConfirmModalProps): React.JSX.Element {
  return (
    <Modal
      open={open}
      title={title}
      description={body}
      busy={busy}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={busy}
            className="bg-danger-fg text-fg-inverse hover:bg-brand-hover active:bg-brand-hover"
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </>
      }
    />
  );
}
